/**
 * Tabs — hand-rolled, useState-based (no shadcn/radix).
 * Simple controlled tab primitive reusable across T5 + T2.
 * API mirrors shadcn/radix (Tabs/TabsList/TabsTrigger/TabsContent) so
 * consumption code looks familiar.
 *
 * Two visual variants on TabsList (design.md §5 — do not mix the patterns):
 * - `pill` (default) — the pre-U1 look, FROZEN verbatim so existing screens
 *   don't shift. U3 decides per screen which lists become segmented controls
 *   (view reconfiguration → <SegmentedControl />) vs underline tabs.
 * - `underline` — switches content sections WITHIN one entity (customer
 *   detail: Übersicht / Policen / Rechnungen / Struktur): transparent list,
 *   active trigger = 2px --primary bottom border + primary label, no fill.
 * The variant flows to TabsTrigger via context — no per-trigger prop needed.
 */
import { createContext, useContext, type HTMLAttributes, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

interface TabsContext {
  value: string;
  onValueChange: (v: string) => void;
}

const Ctx = createContext<TabsContext | null>(null);

function useTabs() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('Tabs sub-component used outside <Tabs>');
  return ctx;
}

export type TabsVariant = 'pill' | 'underline';

const VariantCtx = createContext<TabsVariant>('pill');

export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  onValueChange: (v: string) => void;
}

export function Tabs({ value, onValueChange, className, children, ...props }: TabsProps) {
  return (
    <Ctx.Provider value={{ value, onValueChange }}>
      <div className={cn('flex flex-col', className)} {...props}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
  variant?: TabsVariant;
}

export function TabsList({ className, variant = 'pill', ...props }: TabsListProps) {
  return (
    <VariantCtx.Provider value={variant}>
      <div
        role="tablist"
        className={cn(
          variant === 'pill'
            ? // FROZEN pre-U1 pill classes — do not change without a story.
              'flex h-auto w-fit items-center gap-1 rounded-md bg-muted/60 p-1'
            : 'flex w-fit items-center gap-4 rounded-none border-b border-border p-0',
          className,
        )}
        {...props}
      />
    </VariantCtx.Provider>
  );
}

export interface TabsTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export function TabsTrigger({ value, className, children, ...props }: TabsTriggerProps) {
  const ctx = useTabs();
  const variant = useContext(VariantCtx);
  const active = ctx.value === value;
  return (
    <button
      role="tab"
      aria-selected={active}
      type="button"
      onClick={() => ctx.onValueChange(value)}
      className={cn(
        variant === 'pill'
          ? // FROZEN pre-U1 pill classes — do not change without a story.
            cn(
              'inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-background text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )
          : cn(
              'inline-flex items-center gap-1 rounded-none border-b-2 border-transparent -mb-px px-1 pb-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground',
            ),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
}

export function TabsContent({ value, className, children, ...props }: TabsContentProps) {
  const ctx = useTabs();
  if (ctx.value !== value) return null;
  return (
    <div role="tabpanel" className={cn(className)} {...props}>
      {children}
    </div>
  );
}
